import React, { useState } from "react";
import { Check, Minus } from "lucide-react";
import { Button } from "./ui";
import { useMoney } from "../lib/currency";

// ---------- Conversation transcript (layout signature #4) ----------
export function ConversationTranscript({ channel = "WhatsApp", messages = [], annotations = [], caption, compact = false }) {
  return (
    <figure className="reveal-on-scroll">
      <div className="rounded-lg border border-line bg-white overflow-hidden">
        <div className="flex items-center justify-between border-b border-line bg-surface-sunk px-4 py-2.5">
          <span className="font-mono text-2xs uppercase tracking-eyebrow text-ink-600">{channel}</span>
          <span className="font-mono text-2xs text-ink-400">Live thread</span>
        </div>
        <div className={`grid ${annotations.length && !compact ? "md:grid-cols-[1fr,220px]" : ""}`}>
          <div className="p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "agent" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] rounded-lg px-3 py-2 text-sm ${m.from === "agent" ? "bg-brand-050 text-ink-900" : "bg-surface-sunk text-ink-800"}`}>
                  <p className="text-ink-800">{m.text}</p>
                  <div className="mt-1 flex items-center gap-1 font-mono text-[10px] text-ink-400">
                    <span>{m.time}</span>
                    {m.from === "agent" && <span aria-label="Read">✓✓</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {annotations.length > 0 && !compact && (
            <aside className="border-t md:border-t-0 md:border-l border-line p-4 bg-surface-sunk/40">
              <div className="font-mono text-2xs uppercase tracking-eyebrow text-ink-400 mb-3">Notes</div>
              <ul className="space-y-3">
                {annotations.map((a, i) => (
                  <li key={i} className="text-xs text-ink-600 border-l-2 border-brand-500 pl-3">{a}</li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </div>
      {caption && <figcaption className="mt-4 font-serif text-2xl text-ink-600">{caption}</figcaption>}
    </figure>
  );
}

// ---------- Stat band ----------
export function StatBand({ stats }) {
  return (
    <dl className="grid grid-cols-2 md:grid-cols-4 border-y border-line">
      {stats.map((s, i) => (
        <div key={s.id || i} className={`p-6 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b md:border-b-0" : ""} ${i > 0 && i !== 2 ? "md:border-l" : ""} border-line`}>
          <dd className="font-mono text-4xl text-ink-950">{s.value}<span className="text-brand-600">{s.suffix}</span></dd>
          <dt className="mt-2 text-xs text-ink-400">{s.label}</dt>
        </div>
      ))}
    </dl>
  );
}

// ---------- Rating histogram (five bars, mono) ----------
export function RatingHistogram({ summary }) {
  const { count, average, distribution } = summary;
  const max = Math.max(1, ...Object.values(distribution || {}));
  return (
    <div className="flex flex-col sm:flex-row gap-8 items-start">
      <div>
        <div className="font-mono text-6xl text-ink-950">{average}</div>
        <div className="mt-1 font-mono text-2xs uppercase tracking-eyebrow text-ink-400">{count} reviews</div>
      </div>
      <div className="flex-1 w-full max-w-sm space-y-1.5">
        {[5, 4, 3, 2, 1].map((r) => (
          <div key={r} className="flex items-center gap-3">
            <span className="font-mono text-2xs text-ink-400 w-3">{r}</span>
            <div className="h-2 flex-1 bg-surface-sunk rounded-xs overflow-hidden">
              <div className="h-full bg-brand-600" style={{ width: `${((distribution?.[r] || 0) / max) * 100}%` }} />
            </div>
            <span className="font-mono text-2xs text-ink-400 w-6 text-right">{distribution?.[r] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Testimonial wall (hairline cards, no avatars/stars) ----------
export function TestimonialWall({ items }) {
  return (
    <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [&>*]:mb-6">
      {items.map((t) => (
        <article key={t.id} className="break-inside-avoid card-hairline p-6">
          {t.headline && <h3 className="text-lg text-ink-950 mb-2">{t.headline}</h3>}
          <p className="text-ink-800">{t.quote}</p>
          {t.metric && <div className="mt-3 font-mono text-2xs uppercase tracking-eyebrow text-brand-700">{t.metric}</div>}
          <footer className="mt-4 pt-4 border-t border-line flex items-center justify-between">
            <div className="text-xs text-ink-600">{t.author}{t.role ? `, ${t.role}` : ""}{t.company ? ` · ${t.company}` : ""}</div>
            <div className="font-mono text-2xs text-ink-400">{t.rating}/5</div>
          </footer>
          {t.founder_response && (
            <div className="mt-4 ml-4 border-l-2 border-brand-500 pl-3 text-xs text-ink-600">
              <span className="font-mono text-2xs uppercase tracking-eyebrow text-ink-400">Founder response</span>
              <p className="mt-1">{t.founder_response}</p>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

// ---------- Feature comparison cell ----------
export const Yes = () => <Check className="h-4 w-4 text-brand-600 inline" aria-label="Included" />;
export const No = () => <Minus className="h-4 w-4 text-ink-200 inline" aria-label="Not included" />;

// ---------- Pricing table ----------
export function PricingTable({ plans, billing, onSelect }) {
  const money = useMoney();
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {plans.map((p) => (
        <div key={p.slug} className={`card-hairline p-6 flex flex-col ${p.most_popular ? "ring-1 ring-brand-600" : ""}`}>
          <div className="flex items-center justify-between">
            <h3 className="text-xl text-ink-950">{p.name}</h3>
            {p.most_popular && <span className="badge">Most popular</span>}
          </div>
          <div className="mt-4">
            {p.php_monthly == null ? (
              <div className="font-mono text-4xl text-ink-950">Custom</div>
            ) : (
              <div className="font-mono text-4xl text-ink-950">
                {money(billing === "annual" ? p.php_annual : p.php_monthly)}
                <span className="text-sm text-ink-400">/{billing === "annual" ? "yr" : "mo"}</span>
              </div>
            )}
            {billing === "annual" && p.php_monthly != null && p.php_annual != null && (p.php_monthly * 12 - p.php_annual) > 0 && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-brand-600/10 text-brand-700 px-2.5 py-1 text-xs font-medium" data-testid={`plan-annual-save-${p.slug}`}>
                Save {money(p.php_monthly * 12 - p.php_annual)}/yr
                <span className="text-brand-600/70">({Math.round((p.php_monthly * 12 - p.php_annual) / (p.php_monthly * 12) * 100)}% off)</span>
              </div>
            )}
            <div className="mt-1 text-xs text-ink-400">{p.conversations}</div>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-ink-600 flex-1">
            <li className="flex justify-between"><span>Included credits</span><span className="font-mono text-ink-800">{p.included_credits ?? "Negotiated"}</span></li>
            <li className="flex justify-between"><span>Channels</span><span className="font-mono text-ink-800 text-right">{p.channels_label}</span></li>
            <li className="flex justify-between"><span>AI replies / chat</span><span className="font-mono text-ink-800">{p.ai_replies_label}</span></li>
            <li className="flex justify-between"><span>Memory window</span><span className="font-mono text-ink-800">{p.memory_label}</span></li>
            <li className="flex justify-between"><span>Team seats</span><span className="font-mono text-ink-800">{p.team_seats ?? "Unlimited"}</span></li>
          </ul>
          <Button to={p.slug === "enterprise" ? "/contact/?intent=sales" : (onSelect ? undefined : "/pricing/")}
            onClick={p.slug !== "enterprise" && onSelect ? () => onSelect(p) : undefined}
            variant={p.most_popular ? "primary" : "secondary"} size="md" className="mt-6 w-full" data-testid={`plan-cta-${p.slug}`}>
            {p.slug === "enterprise" ? "Contact sales" : (onSelect ? "Subscribe" : "See Pricing")}
          </Button>
        </div>
      ))}
    </div>
  );
}

// ---------- FAQ accordion (details-backed, crawlable) ----------
export function FaqAccordion({ items }) {
  return (
    <div className="divide-y divide-line border-y border-line">
      {items.map((f, i) => (
        <details key={f.id || i} className="group py-4" id={`faq-${i}`}>
          <summary className="flex cursor-pointer items-center justify-between list-none">
            <span className="text-base text-ink-950 pr-6">{f.question || f.q}</span>
            <span className="font-mono text-ink-400 group-open:rotate-45 transition-transform">+</span>
          </summary>
          <div className="mt-3 text-ink-600 text-sm max-w-prose">{f.answer || f.a}</div>
        </details>
      ))}
    </div>
  );
}

// ---------- Generic data table ----------
export function DataTable({ caption, head, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-line">
            {head.map((h, i) => (
              <th key={i} scope="col" className={`py-3 px-3 font-mono text-2xs uppercase tracking-eyebrow text-ink-400 ${i === 0 ? "text-left" : "text-left"}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, y) => (
            <tr key={y} className="border-b border-line align-top">
              {r.map((c, x) => (
                <td key={x} className={`py-3 px-3 text-sm ${x === 0 ? "text-ink-800" : "text-ink-600 font-mono"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
